export type TChatbotConversationHistory = Array<{ role: 'user' | 'assistant'; content: string }>;

type TChatbotUserMessage = {
  type: 'user_message';
  text: string;
} & ({ conversation_id: string } | { conversation_history: TChatbotConversationHistory });

type TChatbotRequestInsightsMessage = {
  type: 'request_insights';
  rows: TChatbotTableRows;
  run_id: string;
} & ({ conversation_id: string } | { conversation_history: TChatbotConversationHistory });

export type TChatbotSendMessage =
  | TChatbotUserMessage
  | TChatbotRequestInsightsMessage
  | { type: 'request_initial_questions' }
  | { type: 'cancel'; conversation_id: string }
  | { type: 'feedback'; run_id: string; rating: boolean }
  | { type: 'ping' };

export type TChatbotTableRows = Array<Partial<Record<string, string | number>>>;

export type TChatbotRunStageName =
  | 'resolving_entities'
  | 'generating_query'
  | 'running_query'
  | 'repairing_query'
  | 'building_chart'
  | 'writing_summary'
  | 'generating_insights';

export type TChatbotRunStageStatus = 'started' | 'done' | 'failed' | 'skipped';

export type TChatbotReceiveMessage =
  | { type: 'ready'; conversation_id: string }
  | { type: 'initial_questions'; heading: string; questions: string[] }
  | { type: 'run_started'; run_id: string; planned_stages: Array<{ name: TChatbotRunStageName; label: string }> }
  | { type: 'stage'; label: string; status: TChatbotRunStageStatus; name: TChatbotRunStageName }
  | { type: 'token'; text: string }
  | { type: 'visualization'; highcharts: Highcharts.Options }
  | { type: 'result'; headers: string[]; rows: TChatbotTableRows }
  | { type: 'insights'; insights: Array<{ heading: string; text: string }> }
  | { type: 'explanation'; text: string }
  | { type: 'insights_finished' }
  | { type: 'run_cancelled' }
  | { type: 'run_finished' }
  | { type: 'error'; message: string; retryable: boolean }
  | { type: 'unknown_conversation_id' }
  | { type: 'session_terminated' }
  | { type: 'pong' };

const RECEIVE_MESSAGE_TYPE_MAP: Record<TChatbotReceiveMessage['type'], true> = {
  ready: true,
  initial_questions: true,
  run_started: true,
  stage: true,
  token: true,
  visualization: true,
  result: true,
  insights: true,
  explanation: true,
  insights_finished: true,
  run_cancelled: true,
  run_finished: true,
  error: true,
  unknown_conversation_id: true,
  session_terminated: true,
  pong: true,
};

export const isReceiveMessageTypeValid = (type: string): type is TChatbotReceiveMessage['type'] =>
  RECEIVE_MESSAGE_TYPE_MAP[type as TChatbotReceiveMessage['type']] === true;
